import React from "react";
import { Box, Typography, useMediaQuery,  } from "@mui/material";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";

export const BentoGrid = ({ children, sx = {}, className }) => {
  return (
    <Box
      className={className}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",                          // 1 column on mobile
          sm: "repeat(2, 1fr)",               // 2 columns on tablet
          md: "repeat(3, 1fr)",               // 3 columns on desktop
        },
        gap: { xs: 2, sm: 2.5, md: 3 },
        width: "100%",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export const BentoGridItem = ({
  title,
  description,
  header,
  icon,
  gradient,
  colSpan = 1,
  rowSpan = 1,
  className,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Calculate responsive colSpan
  const responsiveColSpan = isMobile ? 1 : isTablet ? (colSpan === 2 ? 2 : 1) : colSpan;
  const responsiveRowSpan = isMobile ? 1 : rowSpan;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      whileHover={isMobile ? {} : { y: -8, transition: { duration: 0.2 } }}
      style={{
        gridColumn: `span ${responsiveColSpan}`,
        gridRow: `span ${responsiveRowSpan}`,
      }}
    >
      <Box
        className={className}
        sx={{
          height: "100%",
          minHeight: { 
            xs: "160px", 
            sm: "180px", 
            md: rowSpan > 1 ? "320px" : "200px" 
          },
          background: "white",
          borderRadius: { xs: "16px", sm: "18px", md: "20px" },
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          transition: "all 0.3s ease",
          cursor: "pointer",
          "&:hover": {
            boxShadow: { xs: "0 4px 20px rgba(0,0,0,0.06)", sm: "0 20px 40px rgba(0,0,0,0.1)" },
          },
        }}
      >
        {/* Header/Visual area */}
        {header && (
          <Box
            sx={{
              flex: 1,
              minHeight: { xs: "80px", sm: "100px", md: "120px" },
              background: gradient
                ? `linear-gradient(135deg, ${gradient.split(" ")[1]}08, ${gradient.split(" ")[3]}12)`
                : "linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.08) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            {header}
          </Box>
        )}

        {/* Content area */}
        <Box
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            transition: "transform 0.2s",
            "&:hover": {
              transform: { xs: "none", sm: "translateX(4px)" },
            },
          }}
        >
          {/* Icon */}
          {icon && (
            <Box
              sx={{
                width: { xs: 36, sm: 40, md: 44 },
                height: { xs: 36, sm: 40, md: 44 },
                borderRadius: { xs: "10px", sm: "11px", md: "12px" },
                background: gradient || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                mb: { xs: 1.5, md: 2 },
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              {icon}
            </Box>
          )}

          {/* Title */}
          <Typography
            sx={{
              fontWeight: 600,
              color: "#0f172a",
              fontSize: { xs: "0.9375rem", sm: "0.9625rem", md: "1rem" },
              mb: { xs: 0.5, md: 1 },
            }}
          >
            {title}
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              color: "#64748b",
              fontSize: { xs: "0.8125rem", sm: "0.84375rem", md: "0.875rem" },
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: { xs: 3, sm: colSpan === 2 ? 4 : 3, md: colSpan === 2 ? 4 : 3 },
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};

export default BentoGrid;